// ─── Prompt attachments: file upload + dictation ──────────────────────────────
// Shared by both places a user can write a prompt — the home hero box
// (components/home/AIBuilderSection.jsx) and the in-workspace follow-up box
// (components/GenerationView.jsx).
//
// It lives here because the two boxes had drifted badly: the workspace had a
// real implementation while the home box only *looked* like one — it counted
// the picked files for a toast and dropped them, and its "recorder" was a
// three-second setTimeout with no microphone involved. One module means a fix
// to either behaviour lands in both.

/** The generate route rejects anything larger (see routes/generate.js). */
export const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024;

/**
 * Reads a picked file into the shape /api/generate expects.
 *
 * Images are the only attachment the generator can actually use — the prompt is
 * sent to a vision model as a base64 block. Anything else is rejected here
 * rather than accepted into the UI and silently dropped at send time, which is
 * what the workspace box used to do with PDFs and text files.
 *
 * @param {File} file
 * @returns {Promise<{file: File, image: {base64: string, mimeType: string, icon?: string}}>}
 *   `icon` is the image drawn at tab-icon size (see attachmentIcon) — present
 *   only where the browser could decode it
 * @throws {Error} with a message intended for the user
 */
export async function readImageAttachment(file) {
  const read = await readAsBase64(file);
  const icon = await attachmentIcon(file);
  return icon ? { ...read, image: { ...read.image, icon } } : read;
}

const ICON_PX = 128;

/**
 * The attachment drawn onto a 128px transparent square, as a PNG data URL.
 *
 * If this image is the owner's logo, it becomes the site's tab icon
 * (builderslab/server/services/logoIcon.js). The server has no image library —
 * a native one is what breaks a Hostinger install — so the resize happens
 * here, where the browser already decodes the file. Contained, not cropped: a
 * wide wordmark stays whole, centred on transparency.
 *
 * Returns '' wherever it cannot: a format the browser cannot decode (HEIC on
 * most), no canvas (tests, old browsers), or a decode that never finishes.
 * The site then keeps the icon the model draws, exactly as before.
 */
export async function attachmentIcon(file) {
  if (typeof document === 'undefined' || typeof URL?.createObjectURL !== 'function') return '';
  const url = URL.createObjectURL(file);
  try {
    const img = await Promise.race([
      new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('undecodable'));
        el.src = url;
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000)),
    ]);
    // An SVG with only a viewBox reports no intrinsic size; draw it square.
    const w = img.naturalWidth || ICON_PX;
    const h = img.naturalHeight || ICON_PX;
    const canvas = document.createElement('canvas');
    canvas.width = ICON_PX;
    canvas.height = ICON_PX;
    const ctx = canvas.getContext?.('2d');
    if (!ctx) return '';
    const scale = Math.min(ICON_PX / w, ICON_PX / h);
    const dw = Math.round(w * scale);
    const dh = Math.round(h * scale);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, Math.round((ICON_PX - dw) / 2), Math.round((ICON_PX - dh) / 2), dw, dh);
    const png = canvas.toDataURL('image/png');
    return png.startsWith('data:image/png;base64,') ? png : '';
  } catch {
    return '';
  } finally {
    URL.revokeObjectURL(url);
  }
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file selected.'));
      return;
    }
    if (!file.type || !file.type.startsWith('image/')) {
      reject(new Error('Only images can be attached — try a PNG, JPG or WebP.'));
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      reject(new Error('Image must be under 4 MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = String(ev.target?.result || '');
      const base64 = dataUrl.split(',')[1];
      // A truncated or unreadable file yields a data URI with no payload; the
      // server would reject it, so fail here where we can still say why.
      if (!base64) {
        reject(new Error('That image could not be read. Please try another.'));
        return;
      }
      resolve({ file, image: { base64, mimeType: file.type } });
    };
    reader.onerror = () => reject(new Error('That image could not be read. Please try another.'));
    reader.readAsDataURL(file);
  });
}

/** The browser's speech recogniser, or null where there is none (e.g. Firefox). */
function recognizerCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/** True when this browser can transcribe speech at all. */
export function speechRecognitionAvailable() {
  return !!recognizerCtor();
}

/**
 * Starts a one-shot dictation.
 *
 * @param {object} [handlers]
 * @param {(transcript: string) => void} [handlers.onTranscript] called with the recognised text
 * @param {() => void} [handlers.onEnd] called when the session ends, however it ends
 * @param {(event: any) => void} [handlers.onError] called with the recogniser's error event
 * @param {string} [handlers.lang] BCP-47 tag; defaults to en-US
 * @returns {object|null} the live recogniser — call `.stop()` to end it early —
 *   or null when the browser has no speech support, so the caller can say so
 *   instead of leaving a dead button.
 */
export function startDictation({ onTranscript, onEnd, onError, lang = 'en-US' } = {}) {
  const SR = recognizerCtor();
  if (!SR) return null;

  const recognition = new SR();
  recognition.continuous    = false;
  recognition.interimResults = false;
  recognition.lang          = lang;

  recognition.onresult = (e) => {
    const transcript = e?.results?.[0]?.[0]?.transcript;
    if (transcript) onTranscript?.(transcript);
  };
  // onend fires after a result, a stop() and most errors alike, so it is the
  // one reliable place to clear a "listening" indicator.
  recognition.onend   = () => onEnd?.();
  recognition.onerror = (e) => onError?.(e);

  recognition.start();
  return recognition;
}

/**
 * Adds dictated text to whatever the user already typed, rather than replacing
 * it — speaking is usually a second thought on top of a written prompt.
 */
export function appendTranscript(existing, transcript) {
  const base = String(existing || '').trim();
  const addition = String(transcript || '').trim();
  if (!addition) return existing || '';
  return base ? `${base} ${addition}` : addition;
}

/** Turns a recogniser error event into something worth showing a user. */
export function dictationErrorMessage(event) {
  switch (event?.error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was blocked. Allow it in your browser settings to dictate.';
    case 'no-speech':
      return 'No speech detected — try again.';
    case 'audio-capture':
      return 'No microphone found.';
    case 'network':
      return 'Speech recognition needs a network connection.';
    case 'aborted':
      return '';   // the user stopped it deliberately; saying anything is noise
    default:
      return 'Dictation failed. Please try again.';
  }
}
